import { LiveGameRouteSkeleton } from '@/components/molecules/live-game-route-skeleton';
import { GameClearModal } from '@/components/shared/GameClearModal';
import { SkipConfirmModal } from '@/components/shared/SkipConfirmModal';
import { MazeTemplate } from '@/components/templates/maze-template';
import {
  GAME_RESULT_VARIANT,
  GAME_SESSION_STATUS,
  MAZE_DEMO_COMPLETE_EXIT_DELAY_MS,
  MAZE_FLIP_PHASE,
  type MazeFlipPhase,
} from '@/constants/maze';
import { liveGameEngine } from '@/features/live-engine/live-game-engine';
import { useLiveGameBootstrap } from '@/hooks/use-live-game-bootstrap';
import { useMazeAudio } from '@/hooks/use-maze-audio';
import { useMazeBgmPreload } from '@/hooks/use-maze-bgm-preload';
import { useMazeTimer } from '@/hooks/use-maze-timer';
import { applyMazePhaseAudio } from '@/lib/audio/maze-audio-phase';
import { useDemoStore } from '@/store/demo';
import { useLiveStore } from '@/store/live';
import { usePreviewStore } from '@/store/preview';
import { useSettingsStore } from '@/store/settings';
import { STAGE_THEME_COLORS } from '@/theme/stage-colors';
import type { GameSessionResponse } from '@/types/api/game';
import { MAZE_AUDIO_EVENT } from '@/types/maze-audio-events';
import { MazeGamePhase } from '@/types/maze-phase';
import type { StageId } from '@/types/stage-theme';
import {
  isMazePlayModeDemo,
  isMazePlayModePreview,
  type MazePlayMode,
} from '@/utils/maze/maze-play-mode';
import { getPreloadedAssetUrl } from '@/utils/asset-manifest';
import { secondsRemainingUntil } from '@/utils/time/expiry-timer';
import { paths } from '@app/router/routes';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigate } from '@/hooks/use-app-navigate';
import { useShallow } from 'zustand/react/shallow';

interface MazeSectionProps {
  readonly mode: MazePlayMode;
  readonly stageId: StageId;
}

const FLIP_HALF_S = 0.42;
const FLIP_WAIT_MS = Math.round(FLIP_HALF_S * 1000) + 80;

export function MazeSection({ mode, stageId }: Readonly<MazeSectionProps>) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const isDemo = isMazePlayModeDemo(mode);
  const isPreview = isMazePlayModePreview(mode);

  const demoPhase = useDemoStore((s) => s.phase);
  const livePhase = useLiveStore((s) => s.phase);
  const previewPhase = usePreviewStore((s) => s.phase);
  const selectPhase = () => {
    if (isDemo) return demoPhase;
    if (isPreview) return previewPhase;
    return livePhase;
  };
  const phase = selectPhase();

  const demoTimer = useDemoStore((s) => s.timer);
  const liveTimer = useLiveStore((s) => s.timer);
  const previewTimer = usePreviewStore((s) => s.timer);
  const selectTimer = () => {
    if (isDemo) return demoTimer;
    if (isPreview) return previewTimer;
    return liveTimer;
  };
  const timer = selectTimer();
  const demoLevel = useDemoStore((s) => s.level);
  const demoSession = useDemoStore((s) => s.demoSession);
  const liveLevel = useLiveStore((s) => s.level);
  const previewLevel = usePreviewStore((s) => s.level);
  const selectLevel = () => {
    if (isDemo) return demoLevel;
    if (isPreview) return previewLevel;
    return liveLevel;
  };
  const level = selectLevel();
  const soundEffectsEnabled = useSettingsStore((s) => s.soundEffectsEnabled);
  const setSoundEffectsEnabled = useSettingsStore((s) => s.setSoundEffectsEnabled);
  const controlsEnabled = useSettingsStore((s) => s.controlsEnabled);
  const setControlsEnabled = useSettingsStore((s) => s.setControlsEnabled);

  const resetGame = useDemoStore((s) => s.resetGame);
  const goToStart = useDemoStore((s) => s.goToStart);
  const {
    advanceRoundAfterReachingEnd,
    fetchStatus,
    clearLiveSession,
    endGameEarly,
    publishTerminalResult,
    setResult,
    startLivePlaying,
    prepareLiveGameRouteEntry,
    hasActiveLiveSession,
  } = useLiveStore(
    useShallow((s) => ({
      advanceRoundAfterReachingEnd: s.advanceRoundAfterReachingEnd,
      fetchStatus: s.fetchStatus,
      clearLiveSession: s.clearLiveSession,
      endGameEarly: s.endGameEarly,
      publishTerminalResult: s.publishTerminalResult,
      setResult: s.setResult,
      startLivePlaying: s.startLivePlaying,
      prepareLiveGameRouteEntry: s.prepareLiveGameRouteEntry,
      hasActiveLiveSession: s.hasActiveLiveSession,
    })),
  );
  const liveSession = useLiveStore((s) => s.session);
  const liveLives = useLiveStore((s) => s.lives);
  const demoCurrentLevelIndex = useDemoStore((s) => s.currentLevelIndex);

  const isLastBoard = useMemo(() => {
    if (isPreview) return true;
    if (isDemo) return demoSession ? demoCurrentLevelIndex >= demoSession.levels.length - 1 : false;
    return liveSession ? (liveSession.maze?.roundNumber ?? 1) >= liveSession.totalRounds : false;
  }, [isPreview, isDemo, demoSession, demoCurrentLevelIndex, liveSession]);

  const applyLiveSessionToHud = useCallback(
    (data: GameSessionResponse) => {
      const remaining = secondsRemainingUntil(data.expiryAt);
      startLivePlaying(data.maze?.roundNumber ?? 1, remaining, data.scoreboard.totalScore);
    },
    [startLivePlaying],
  );

  const { playBgm, stopBgm, playLose, playMove, playPickSide, playWin } =
    useMazeAudio(soundEffectsEnabled);
  const [isStartFreshPending, setIsStartFreshPending] = useState(false);
  const [isDemoExitPending, setIsDemoExitPending] = useState(false);
  const [isSkipPending, setIsSkipPending] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [levelCompleteBurst, setLevelCompleteBurst] = useState(false);
  const [flipPhase, setFlipPhase] = useState<MazeFlipPhase>(MAZE_FLIP_PHASE.IDLE);
  const [stopAnimation, setStopAnimation] = useState(false);
  const stopAnimationRef = useRef(false);
  const isLastBoardRef = useRef(isLastBoard);
  const burstTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const flipTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const suppressLiveStatusBootstrapRef = useRef(false);
  const liveBootstrapFinishedRef = useRef(false);
  const [liveBootstrapUiReady, setLiveBootstrapUiReady] = useState(
    () =>
      isDemo ||
      isPreview ||
      (hasActiveLiveSession && liveSession?.status === GAME_SESSION_STATUS.STARTED),
  );

  useMazeTimer(mode);

  useLayoutEffect(() => {
    if (isDemo || isPreview || liveBootstrapUiReady) {
      return;
    }
    prepareLiveGameRouteEntry();
  }, [isDemo, isPreview, liveBootstrapUiReady, prepareLiveGameRouteEntry]);

  useLayoutEffect(() => {
    if (!isDemo || !demoSession?.enableDemo) {
      return;
    }
    if (demoPhase !== MazeGamePhase.START) {
      return;
    }
    resetGame();
  }, [demoPhase, demoSession, isDemo, resetGame]);

  useEffect(() => {
    if (!isDemo && !isPreview) {
      suppressLiveStatusBootstrapRef.current = false;
    }
  }, [isDemo, isPreview, stageId]);

  useEffect(() => {
    if (isDemo || isPreview || !liveBootstrapUiReady || phase === MazeGamePhase.PLAYING) {
      return;
    }

    const { session, hasActiveLiveSession } = useLiveStore.getState();
    if (hasActiveLiveSession && session?.status === GAME_SESSION_STATUS.STARTED) {
      applyLiveSessionToHud(session);
    }
  }, [applyLiveSessionToHud, isDemo, isPreview, liveBootstrapUiReady, phase]);

  useLiveGameBootstrap({
    isDemo,
    isPreview,
    stageId,
    suppressLiveStatusBootstrapRef,
    liveBootstrapFinishedRef,
    setLiveBootstrapUiReady,
    applyLiveSessionToHud,
    fetchStatus,
    clearLiveSession,
    navigate,
    setResult,
    advanceRoundAfterReachingEnd,
  });

  useLayoutEffect(() => {
    if (isDemo || isPreview || (phase !== MazeGamePhase.WIN && phase !== MazeGamePhase.LOSE)) {
      return;
    }
    publishTerminalResult(
      stageId,
      phase === MazeGamePhase.WIN ? GAME_RESULT_VARIANT.SUCCESS : GAME_RESULT_VARIANT.FAILURE,
    );
    navigate(paths.results, { replace: true });
  }, [isDemo, isPreview, navigate, phase, publishTerminalResult, stageId]);

  useEffect(() => {
    if (!soundEffectsEnabled) {
      return;
    }

    applyMazePhaseAudio(phase, { playBgm, stopBgm, playWin, playLose });
  }, [phase, playBgm, playLose, playWin, soundEffectsEnabled, stopBgm]);

  useEffect(() => {
    const onMove = () => {
      playMove();
    };
    const onPickSide = () => {
      playPickSide();
    };
    const onGoalReached = () => {
      playWin();
      if (burstTimerRef.current) globalThis.clearTimeout(burstTimerRef.current);
      if (flipTimerRef.current) globalThis.clearTimeout(flipTimerRef.current);

      setLevelCompleteBurst(true);
      burstTimerRef.current = globalThis.setTimeout(() => {
        setLevelCompleteBurst(false);
      }, 1500);

      if (!stopAnimationRef.current && !isPreview && !isLastBoardRef.current) {
        setFlipPhase(MAZE_FLIP_PHASE.FOLDING);
        flipTimerRef.current = globalThis.setTimeout(() => {
          setFlipPhase(MAZE_FLIP_PHASE.UNFOLDING);
          flipTimerRef.current = globalThis.setTimeout(() => {
            setFlipPhase(MAZE_FLIP_PHASE.IDLE);
          }, FLIP_WAIT_MS);
        }, FLIP_WAIT_MS);
      }
    };

    globalThis.window.addEventListener(MAZE_AUDIO_EVENT.move, onMove);
    globalThis.window.addEventListener(MAZE_AUDIO_EVENT.pickSide, onPickSide);
    globalThis.window.addEventListener(MAZE_AUDIO_EVENT.goalReached, onGoalReached);

    return () => {
      globalThis.window.removeEventListener(MAZE_AUDIO_EVENT.move, onMove);
      globalThis.window.removeEventListener(MAZE_AUDIO_EVENT.pickSide, onPickSide);
      globalThis.window.removeEventListener(MAZE_AUDIO_EVENT.goalReached, onGoalReached);
      if (burstTimerRef.current) globalThis.clearTimeout(burstTimerRef.current);
      if (flipTimerRef.current) globalThis.clearTimeout(flipTimerRef.current);
    };
  }, [playMove, playPickSide, playWin, isPreview]);

  useEffect(() => {
    isLastBoardRef.current = isLastBoard;
  }, [isLastBoard]);

  useEffect(() => {
    const onSkipStart = () => {
      if (burstTimerRef.current) globalThis.clearTimeout(burstTimerRef.current);
      if (flipTimerRef.current) globalThis.clearTimeout(flipTimerRef.current);

      setIsSkipPending(true);
      setFlipPhase(MAZE_FLIP_PHASE.FOLDING);
    };

    const onSkipComplete = () => {
      if (flipTimerRef.current) globalThis.clearTimeout(flipTimerRef.current);

      setFlipPhase(MAZE_FLIP_PHASE.UNFOLDING);
      flipTimerRef.current = globalThis.setTimeout(() => {
        setFlipPhase(MAZE_FLIP_PHASE.IDLE);
        setIsSkipPending(false);
      }, FLIP_WAIT_MS);
    };

    globalThis.window.addEventListener('maze-skip-start', onSkipStart);
    globalThis.window.addEventListener('maze-skip-complete', onSkipComplete);

    return () => {
      globalThis.window.removeEventListener('maze-skip-start', onSkipStart);
      globalThis.window.removeEventListener('maze-skip-complete', onSkipComplete);
    };
  }, []);

    useEffect(() => {
      stopAnimationRef.current = stopAnimation;
    }, [stopAnimation]);

    useEffect(() => {
      if (stopAnimation) {
        if (flipTimerRef.current) {
          globalThis.clearTimeout(flipTimerRef.current);
          flipTimerRef.current = null;
        }
        setFlipPhase(MAZE_FLIP_PHASE.IDLE);
      }
    }, [stopAnimation]);

  const handleDone = useCallback(() => {
    setIsDemoExitPending(true);
    resetGame(stageId);
    navigate(paths.home, { replace: true });
  }, [navigate, resetGame, stageId]);

  const handlePreviewDone = useCallback(() => {
    navigate(paths.home, { replace: true });
  }, [navigate]);

  const handleSkip = useCallback(() => {
    if (isSkipPending || phase !== MazeGamePhase.PLAYING || liveLives <= 0) {
      return;
    }
    globalThis.window.dispatchEvent(new CustomEvent('maze-skip-trigger'));
  }, [isSkipPending, phase, liveLives]);

  const handleStartFresh = useCallback(async () => {
    if (isStartFreshPending) {
      return;
    }
    setIsStartFreshPending(true);
    if (isPreview) {
      navigate(paths.home, { replace: true });
      return;
    }
    if (!isDemo) {
      suppressLiveStatusBootstrapRef.current = true;
    }
    try {
      if (!isDemo && hasActiveLiveSession) {
        await endGameEarly({ refreshStatus: false }).catch(() => null);
      }
      clearLiveSession();
      await goToStart(stageId);
      navigate(paths.home, { replace: true });
    } catch {
      setIsStartFreshPending(false);
    }
  }, [
    clearLiveSession,
    endGameEarly,
    goToStart,
    hasActiveLiveSession,
    isDemo,
    isPreview,
    isStartFreshPending,
    navigate,
    stageId,
  ]);

  const resolveHudOnDone = (): (() => void) | undefined => {
    if (isDemo) {
      return handleDone;
    }
    if (isPreview) {
      return handlePreviewDone;
    }
    return undefined;
  };
  const hudOnDone = resolveHudOnDone();

  const skeletonProps = {
    interactiveHud: true as const,
    playMode: mode,
    level,
    timer,
    soundEffectsEnabled,
    controlsEnabled,
    onToggleSoundEffects: setSoundEffectsEnabled,
    onToggleControls: setControlsEnabled,
    onStartFresh: handleStartFresh,
    isStartFreshPending,
    onDone: hudOnDone,
  };

  const isTerminalPhase = phase === MazeGamePhase.WIN || phase === MazeGamePhase.LOSE;
  const isLiveGameReady =
    !isDemo &&
    !isPreview &&
    liveBootstrapUiReady &&
    phase === MazeGamePhase.PLAYING &&
    liveSession?.status === GAME_SESSION_STATUS.STARTED &&
    liveSession.maze !== null &&
    liveSession.maze !== undefined;
  const isDemoGameReady = isDemo && phase === MazeGamePhase.PLAYING;
  const isPreviewGameReady = isPreview && (phase === MazeGamePhase.PLAYING || phase === MazeGamePhase.WIN);
  const isGameReady = isDemoGameReady || isPreviewGameReady || isLiveGameReady;
  const showPlay = !isStartFreshPending && !isDemoExitPending && (!isTerminalPhase || (isPreview && phase === MazeGamePhase.WIN)) && isGameReady;
  const showSkeleton =
    isStartFreshPending ||
    isDemoExitPending ||
    (isDemo && phase === MazeGamePhase.WIN) ||
    (!isTerminalPhase && !isGameReady);
  const mazeExitRedirectSeconds = Math.ceil(MAZE_DEMO_COMPLETE_EXIT_DELAY_MS / 1000);
  const winExitTheme = STAGE_THEME_COLORS[stageId];

  useMazeBgmPreload(isGameReady && !isTerminalPhase);

  return (
    <>
      <div
        className={`background-layer${levelCompleteBurst && !stopAnimation ? ' level-complete-active' : ''}`}
        style={{ backgroundImage: `url(${getPreloadedAssetUrl('/assets/mandala-vector.png')})` }}
      />
      {levelCompleteBurst && !stopAnimation ? <div className="level-complete-burst" /> : null}
      {showSkeleton || !showPlay ? (
        <LiveGameRouteSkeleton {...skeletonProps} />
      ) : (
        <MazeTemplate
          phase={phase}
          level={level}
          timer={timer}
          playMode={mode}
          soundEffectsEnabled={soundEffectsEnabled}
          controlsEnabled={controlsEnabled}
          onToggleSoundEffects={setSoundEffectsEnabled}
          onToggleControls={setControlsEnabled}
          onStartFresh={handleStartFresh}
          isStartFreshPending={isStartFreshPending}
          onDone={hudOnDone}
          flipPhase={flipPhase}
          levelCompleteBurst={levelCompleteBurst}
          stopAnimation={stopAnimation}
          onStopAnimationToggle={() => { setStopAnimation((v) => !v); }}
          onSkip={() => setShowSkipConfirm(true)}
          isSkipPending={isSkipPending}
          isTimerEnabled={isDemo || isPreview || liveGameEngine.isTimerEnabled()}
          lives={liveLives}
        />
      )}
      {isDemo && phase === MazeGamePhase.WIN ? (
        <GameClearModal
          title={t('toast.demoCompleteTitle')}
          accentColor={winExitTheme.resultAccent}
          eclipseColor={winExitTheme.eclipse}
          redirectDestination={t('toast.redirectDestinationShort')}
          countdownSecs={mazeExitRedirectSeconds}
          onConfirm={() => {
            navigate(paths.home, { replace: true });
          }}
        />
      ) : null}
      {isPreview && phase === MazeGamePhase.WIN ? (
        <GameClearModal
          title="Preview Completed"
          accentColor={winExitTheme.resultAccent}
          eclipseColor={winExitTheme.eclipse}
          noCountdown={true}
          onConfirm={() => {}}
        />
      ) : null}

      <SkipConfirmModal
        isOpen={showSkipConfirm}
        onCancel={() => setShowSkipConfirm(false)}
        onConfirm={() => {
          setShowSkipConfirm(false);
          handleSkip();
        }}
      />
    </>
  );
}
